import { inject, Service } from '@angular/core'

import { VenuesService } from '@features/venues'
import { MessagesService } from '@shared/services'
import { Movement } from '../../models/tickets.model'
import { HistoryApi } from '../history-api/history-api'

/** Los tickets de un mes, tal como los cuenta el resumen del home. */
export interface TicketsSummary {
  approved: number
  rejected: number
  /** Aprobados y rechazados del mes, del más reciente al más antiguo. */
  tickets: Movement[]
}

/**
 * Tickets (compras y rechazos, no canjes) del mes de `now`. Recibe los
 * movimientos ya ordenados, como los entrega `getMovements`.
 */
export function ticketsOfMonth(movements: Movement[], now: Date): TicketsSummary {
  const tickets = movements.filter(movement => movement.kind !== 'redemption' && movement.date.getFullYear() === now.getFullYear() && movement.date.getMonth() === now.getMonth())
  const rejected = tickets.filter(ticket => ticket.kind === 'rejected').length

  return { approved: tickets.length - rejected, rejected, tickets }
}

/** Historial sobre `HistoryApi`. Maneja los errores con `MessagesService`; nunca lanza. */
@Service()
export class HistoryService {
  readonly #api = inject(HistoryApi)
  readonly #venues = inject(VenuesService)
  readonly #messages = inject(MessagesService)

  /**
   * Compras y rechazos del centro de consumo, y canjes del participante, del
   * más reciente al más antiguo. Sin centro solo quedan los canjes. Si una
   * fuente falla se avisa y se muestran las demás.
   */
  async getMovements(): Promise<Movement[]> {
    const venue = await this.#venues.getMyVenue()
    const tickets = venue ? [this.#api.getPurchases(venue.id), this.#api.getRejected(venue.id)] : []
    const results = await Promise.allSettled([...tickets, this.#api.getRedemptions()])

    if (results.some(result => result.status === 'rejected')) void this.#messages.error(null, 'No se pudo cargar todo el historial')

    return results.flatMap(result => (result.status === 'fulfilled' ? result.value : [])).sort((a, b) => b.date.getTime() - a.date.getTime())
  }
}
